<template>
  <div class="school-search-list-view">
    <div class="search-top-bar">
      <div class="search-tip">
        关于 “<span class="keyword-highlight">{{ searchKeyword }}</span
        >” 的搜索结果：
      </div>
    </div>

    <!-- 选项卡卡片头 -->
    <div class="search-tab-card">
      <div class="search-tabs">
        <div
          class="search-tab-item"
          :class="{ active: currentTab === 'course' }"
          @click="switchTab('course')"
        >
          课程
        </div>
        <div
          class="search-tab-item"
          :class="{ active: currentTab === 'package' }"
          @click="switchTab('package')"
        >
          课表
        </div>
      </div>
    </div>

    <!-- 内容展示区 -->
    <div class="search-results-content">
      <!-- 加载动画 -->
      <SchoolLoading :visible="loading" text="检索数据中" />

      <!-- 课程渲染列表 -->
      <div v-if="currentTab === 'course'" class="results-wrapper">
        <template v-if="list.length">
          <div class="card-grid">
            <CourseCard
              v-for="(item, index) in list"
              :key="index"
              :item="item"
              :back-ground="backGround"
              :color-enum="colorEnum"
              @click="toDetails"
            />
          </div>
        </template>
        <None v-else />
      </div>

      <!-- 课表 (课程包) 渲染列表 -->
      <div v-else-if="currentTab === 'package'" class="results-wrapper">
        <template v-if="list.length">
          <div class="packages-grid">
            <div
              class="package-card"
              v-for="item in list"
              :key="item.id"
              @click="toPackageDetails(item)"
            >
              <div class="package-cover-wrap">
                <img :src="item.cover" class="package-cover hoverTransform" />
              </div>
              <div class="package-card-content">
                <div class="package-title-wrap">
                  <div class="package-title textOverflowTwo">
                    {{ item.title }}
                  </div>
                </div>
                <div class="package-footer">
                  <div class="package-subs">
                    <i class="el-icon-user"></i>
                    <span>{{ item.subscribeCount || 0 }}人订阅</span>
                  </div>
                  <div class="package-action">立即学习</div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <None v-else />
      </div>

      <!-- 分页器 -->
      <div class="pagination-container" v-if="total > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CourseCard from "./CourseCard.vue";
import { courseInfoList, coursePackageList } from "@/api/lx";
import SchoolLoading from "./SchoolLoading.vue";

export default {
  name: "SearchListView",
  components: {
    CourseCard,
    SchoolLoading,
  },
  props: {
    searchKeyword: {
      type: String,
      default: "",
    },
    backGround: {
      type: Object,
      default: () => ({}),
    },
    colorEnum: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      currentTab: "course", // course, package
      loading: false,
      list: [],
      currentPage: 1,
      pageSize: 12,
      total: 0,
    };
  },
  watch: {
    searchKeyword: {
      immediate: true,
      handler() {
        this.currentPage = 1;
        this.loadData();
      },
    },
  },
  methods: {
    switchTab(tab) {
      if (this.currentTab === tab) return;
      this.currentTab = tab;
      this.currentPage = 1;
      this.list = [];
      this.total = 0;
      this.loadData();
    },
    loadData() {
      if (!this.searchKeyword) return;
      this.loading = true;
      const startTime = Date.now();

      // 确保最少有 500 毫秒的加载效果，防止网络过快时闪动
      const finishLoading = () => {
        const duration = Date.now() - startTime;
        const delay = Math.max(0, 500 - duration);
        setTimeout(() => {
          this.loading = false;
        }, delay);
      };

      if (this.currentTab === "course") {
        // 调用课程分页接口
        courseInfoList({
          title: this.searchKeyword,
          page: this.currentPage,
          size: this.pageSize,
        })
          .then((res) => {
            const data = res.data || {};
            this.list = data.records || [];
            this.total = data.total || 0;
          })
          .catch((err) => {
            console.error("加载搜索课程出错:", err);
          })
          .finally(() => {
            finishLoading();
          });
      } else if (this.currentTab === "package") {
        // 调用课程包分页接口
        coursePackageList({
          title: this.searchKeyword,
          page: this.currentPage,
          size: this.pageSize,
        })
          .then((res) => {
            const data = res.data || {};
            this.list = data.records || [];
            this.total = data.total || 0;
          })
          .catch((err) => {
            console.error("加载搜索课程包出错:", err);
          })
          .finally(() => {
            finishLoading();
          });
      } else {
        // 证书直接清空
        this.list = [];
        this.total = 0;
        finishLoading();
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadData();
      // 滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    toDetails(item) {
      this.$emit("to-details", item);
    },
    toPackageDetails(item) {
      this.$emit("to-package-details", item);
    },
    clearSearch() {
      this.$emit("clear-search");
    },
  },
};
</script>

<style lang="less" scoped>
.school-search-list-view {
  width: 100%;
  padding-bottom: 60px;
  box-sizing: border-box;
}

/* Breadcrumb & Tip */
.search-top-bar {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.breadcrumb-container {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #525f66;

  .breadcrumb-item {
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #00d8c3;
    }

    &.active {
      color: #7b8e99;
      cursor: default;
      &:hover {
        color: #7b8e99;
      }
    }
  }

  .breadcrumb-separator {
    width: 6px;
    height: 6px;
    border-top: 1.5px solid #bacac6;
    border-right: 1.5px solid #bacac6;
    transform: rotate(45deg);
    margin: 0 10px;
  }
}

.search-tip {
  font-size: 15px;
  color: #191c1e;
  font-weight: 500;
  .keyword-highlight {
    color: #00d8c3;
    font-weight: 700;
  }
}

/* Tabs Card */
.search-tab-card {
  width: 100%;
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 24px;
  margin-top: 16px;
  box-sizing: border-box;
  box-shadow: 0px 4px 16px rgba(0, 22, 26, 0.04);
}

.search-tabs {
  display: flex;
  gap: 10px;
}

.search-tab-item {
  padding: 8px 24px;
  background: #ffffff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  user-select: none;
  transition: all 0.25s ease;

  &:hover {
    color: #00d8c3;
    border-color: rgba(0, 216, 195, 0.5);
    background: rgba(0, 216, 195, 0.02);
  }

  &.active {
    background: #00d8c3;
    border-color: #00d8c3;
    color: #ffffff;
    font-weight: 700;
    box-shadow: 0px 4px 10px rgba(0, 216, 195, 0.2);
  }
}

/* Results Content Area */
.search-results-content {
  position: relative;
  margin-top: 24px;
  min-height: 300px;
}

.results-wrapper {
  width: 100%;
}

/* Courses Card Grid */
.card-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  box-sizing: border-box;
}

/* Packages Grid (CoursePackage) */
.packages-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  box-sizing: border-box;

  .package-card {
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #f0f3f5;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 30px rgba(0, 22, 26, 0.08);

      .hoverTransform {
        transform: scale(1.05);
      }
      .package-action {
        background: #00d8c3;
        color: #ffffff;
      }
    }

    .package-cover-wrap {
      width: 100%;
      height: 190px;
      overflow: hidden;
      background: #f7fafb;

      .package-cover {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.4s ease;
      }
    }

    .package-card-content {
      padding: 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .package-title-wrap {
      height: 48px;
    }

    .package-title {
      font-size: 15px;
      font-weight: 700;
      color: #191c1e;
      line-height: 22px;
    }

    .package-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .package-subs {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #7b8e99;
      font-size: 13px;
    }

    .package-action {
      padding: 6px 14px;
      border: 1px solid #00d8c3;
      color: #00d8c3;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 700;
      transition: all 0.2s ease;
    }
  }
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}
</style>
